<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col
        v-for="type in ['Meeting', 'Quick Link']"
        :key="type"
        class="d-flex"
      >
        <b-card class="flex-fill" body-class="d-flex flex-column">
          <div class="d-flex mb-4">
            <h1 class="mb-0 mr-auto">{{ type }}s</h1>
            <b-btn
              v-b-tooltip.hover="'Create ' + type"
              class="p-0 font-2xl"
              variant="link"
              @click="$root.$emit('create-meeting', type === 'Meeting')"
            >
              <i class="icon-plus" />
            </b-btn>
          </div>
          <b-table
            :fields="fields[type]"
            :items="items[type]"
            select-mode="single"
            selected-variant="primary"
            hover
            selectable
            show-empty
            @row-selected="selected[type] = $event[0]"
          >
            <template #empty>
              <div class="my-3 text-center">
                <div class="mb-2">You have no {{ type.toLowerCase() }}s</div>
                <b-btn
                  class="px-4"
                  variant="outline-primary"
                  @click="$root.$emit('create-meeting', type === 'Meeting')"
                >
                  Create One
                </b-btn>
              </div>
            </template>
            <template #cell(date)="{ item }">
              {{ formatDateRange(item.start, item.end) }}
            </template>
            <template #cell(exp)="{ item }">
              {{ formatDate(item.exp) }}
            </template>
          </b-table>

          <div class="mt-auto d-flex">
            <b-btn
              class="px-4"
              variant="outline-dark"
              @click="deleteMeeting(selected[type])"
            >
              Delete
            </b-btn>
            <div class="d-flex align-items-center ml-auto mr-4 text-muted">
              {{ items[type].length }} {{ type.toLowerCase()
              }}{{ items[type].length !== 1 ? "s" : "" }}
            </div>
            <b-btn
              :disabled="!selected[type]"
              class="ml-2 px-4"
              variant="outline-dark"
              @click="viewDetails(selected[type])"
            >
              Info
            </b-btn>
            <b-btn
              :disabled="!selected[type]"
              class="ml-2 px-4"
              variant="primary"
              @click="joinMeeting(selected[type])"
            >
              Join
            </b-btn>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-modal
      v-if="meeting"
      ref="modal"
      body-class="bg-gray-100 rounded"
      centered
      hide-header
      hide-footer
      @hidden="showInfo = false"
    >
      <h3 class="mb-3 text-center">
        {{ showInfo ? "" : "Add" }}
        {{ meeting.start ? "Meeting" : "Link" }}
        {{ showInfo ? "Info" : "" }}
      </h3>

      <div class="mb-3 p-4 bg-white rounded">
        <MeetingDetails :meeting="meeting" />
      </div>

      <div v-if="!showInfo" class="text-center">
        <template v-if="!alreadySaved">
          <NanInput
            v-if="!meeting.start"
            v-model="linkExpiration"
            :options="[
              { label: '10 days', value: 10 },
              { label: '30 days', value: 30 },
              { label: '60 days', value: 60 },
              { label: '90 days', value: 90 }
            ]"
            class="mx-auto mb-2 w-50"
            label="Remove from my account after"
            type="select"
          />

          <b-btn class="px-5" variant="outline-primary" @click="addMeeting">
            Add to my {{ meeting.start ? "meetings" : "links" }}
          </b-btn>
        </template>

        <template v-else>
          <div>{{ meeting.start ? "Meeting" : "Link" }} already in account</div>
          <b-btn class="px-5" @click="$refs.modal.hide()">Close</b-btn>
        </template>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import API from "@/api";
import MeetingDetails from "@/components/meetings/MeetingDetails";

export default {
  components: { MeetingDetails },

  metaInfo: {
    titleTemplate: "Meetings - %s"
  },

  props: {
    payload: String
  },

  data: () => ({
    fields: {
      Meeting: [
        { key: "title", label: "Meeting Name" },
        { key: "id", label: "Meeting ID" },
        { key: "date", label: "Date/Time" }
      ],
      "Quick Link": [
        { key: "room", label: "Room Name" },
        { key: "id", label: "Room ID" },
        { key: "exp", label: "Expires" }
      ]
    },
    items: {
      Meeting: [],
      "Quick Link": []
    },
    selected: {
      Meeting: null,
      "Quick Link": null
    },
    linkExpiration: 30,
    meeting: null,
    showInfo: false
  }),

  computed: {
    alreadySaved() {
      if (!this.meeting) return;
      return []
        .concat(...Object.values(this.items))
        .some(m => m.id === this.meeting.id);
    }
  },

  created() {
    this.getMeetings();

    this.$root.$on("refresh-meetings", this.getMeetings);
    this.$once("hook:beforeDestroy", () => {
      this.$root.$off("refresh-meetings", this.getMeetings);
    });
  },

  async mounted() {
    if (this.payload) {
      try {
        this.meeting = JSON.parse(atob(this.payload))[0];
        await this.$nextTick();
        this.$refs.modal.show();
      } catch (err) {
        // do nothing
      }

      this.$router.replace("/meetings");
    }
  },

  methods: {
    async addMeeting() {
      if (!this.meeting.start) {
        this.meeting.exp = moment()
          .add(this.linkExpiration, "days")
          .toISOString();
      }

      const res = await API.addMeeting(this.meeting);
      if (!res.success) return;
      this.$refs.modal.hide();
      this.meeting = null;
      this.getMeetings();
    },

    async deleteMeeting(meeting) {
      const res = await API.deleteMeeting(meeting.id);
      if (!res.success) return;
      this.getMeetings();
    },

    async getMeetings() {
      const res = await API.getMeetings();
      if (!res.success) return;

      this.items["Meeting"] = res.results.filter(m => m.start);
      this.items["Quick Link"] = res.results.filter(m => !m.start);
    },

    joinMeeting(meeting) {
      const keys = [
        "id",
        "room",
        "password",
        "title",
        "description",
        "start",
        "end"
      ];

      const payload = { type: "join" };
      for (const key of keys) {
        if (meeting[key]) payload[key] = meeting[key];
      }

      const url = "https://open.nanome.ai/#/" + btoa(JSON.stringify([payload]));
      window.open(url, "_blank");
    },

    formatDate(date) {
      return moment(date).format("L");
    },

    formatDateRange(start, end) {
      const startText = moment(start).format("L LT");
      const endText = moment(end).format("LT");
      return `${startText} - ${endText}`;
    },

    async viewDetails(meeting) {
      this.meeting = meeting;
      this.showInfo = true;
      await this.$nextTick();
      this.$refs.modal.show();
    }
  }
};
</script>
